/**
 * `GET /account/{id}` response → wizard form state.
 *
 * Mirrors Angular's `editAccount()` shape-flattening: pulls accountResponse
 * fields onto the top level, unpacks whitelisted IP strings back into octets,
 * and rebuilds the `applications[]` rows from `applicationResponse[]`
 * (inferring `notify`/`templatify` flags from the `tag`).
 */

import { createEmptyApplicationRow, EMPTY_IP_OCTETS, createEmptyUser } from '../constants';
import type {
  AccountDetailResponse,
  AccountFormValues,
  AiProvider,
  ApplicationRow,
  IpOctets,
} from '../types/account';

const splitIp = (ip: string): IpOctets => {
  const [o1 = '', o2 = '', o3 = '', o4 = ''] = ip.split('.');
  return { octet1: o1, octet2: o2, octet3: o3, octet4: o4 };
};

const isoToDateString = (s: string | null | undefined): string | null => {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const accountDetailToFormValues = (
  detail: AccountDetailResponse
): AccountFormValues => {
  const ar = detail.accountResponse;
  const rawIps = ar.whiteListedIPs?.iPs ?? [];
  const whitelistApplies = rawIps.length > 0;

  const applications: ApplicationRow[] = detail.applicationResponse.map((app) => ({
    ...createEmptyApplicationRow(),
    applicationId: app.id,
    name: app.name,
    tag: app.tag ?? '',
    notify: app.tag === 'NOTIFY',
    templatify: app.tag === 'TEMPLATIFY' || app.tag === 'VIA_API',
    status: app.syncStatus ?? '',
    isApplicationIntegration: Boolean(ar.useTemplatify),
  }));

  const users = (detail.userResponse ?? []).map((u) => ({
    ...createEmptyUser(),
    ...u,
  }));

  return {
    id: ar.id,
    accountName: ar.accountName ?? '',
    directoryId: ar.directoryId ?? null,
    apiKey: '',
    accountStatus: ar.accountStatus ?? 'active',
    loggingStatus: ar.loggingStatus ?? 'none',
    logRetentionDays: ar.logRetentionDays ?? null,
    licenseId: detail.licenseResponse?.id ?? '',
    validityTimeFrame: isoToDateString(detail.validityTimeFrame),
    providerIds: detail.providerResponse.map((p) => p.id),
    communicationTypes: ar.communicationTypes ?? [],

    isLicenseBlocked: Boolean(detail.isLicenseBlocked),

    rateLimitApplies: Boolean(ar.rateLimitApplies),
    rateLimitPerMin: ar.rateLimitPerMin ?? 0,
    rateLimitPerDay: ar.rateLimitPerDay ?? 0,

    whitelistApplies,
    whiteListedIps: whitelistApplies
      ? rawIps.map(splitIp)
      : [{ ...EMPTY_IP_OCTETS }],

    addUser: users.length > 0,
    users,

    useCostCenter: Boolean(ar.useCostCenter),
    costCenterId: ar.costCenterId ?? null,

    useTemplates: Boolean(ar.useTemplates),

    aiAvailable: Boolean(ar.aiAvailable),
    aiProvider: (ar.aiProvider ?? '') as '' | AiProvider,
    aiApiKeyConfigured: Boolean(ar.aiApiKeyConfigured),
    // Keys never come back from the API — leave empty (empty = keep existing).
    aiApiKey: '',
    aiTranscriptionApiKey: '',

    useTemplatify: Boolean(ar.useTemplatify),
    contactCenterEnabled: Boolean(ar.contactCenterEnabled),
    // Una cuenta anterior a los productos no trae el campo: se asume contratado,
    // que es como se relleno en la migracion.
    portalEnabled: ar.portalEnabled ?? true,
    automationsEnabled: ar.automationsEnabled ?? true,
    maxUsers: ar.maxUsers ?? '',
    contactCenterReplyToEmail: ar.contactCenterReplyToEmail ?? '',
    autoCreateEnabled: ar.autoCreateEnabled ?? true,
    autoCreateGlobal: Boolean(ar.autoCreateGlobal),
    templatifyGroupId: null,
    templatifyExtraApplicationIds: [],
    accountMode: ar.accountMode ?? '',
    rmkTemplateAccountName: ar.rmkTemplateAccountName ?? '',
    rmkTemplateApiKey: ar.rmkTemplateApiKey ?? '',
    rmkExistTemplateAccount: ar.rmkExistTemplateAccount ?? null,
    rmkExistTemplateApiKey: '',
    isUpdateTemplateApiKey: false,

    applications: applications.length > 0 ? applications : [createEmptyApplicationRow()],
    applicationGroups: detail.groupResponse ?? [],
  };
};
